import { CalendarContainer, CalendarYearText, FiscalPickerContainer, MonthButton, MonthsContainer, TertialLabel, TertialRow, YearButton, YearDivider, YearLine, YearNavigationContainer, YearText } from './BomComponent.styles';
import { Control, Controller } from 'react-hook-form';
import React, { useCallback, useState } from 'react';
import { backendToFiscalPeriod, fiscalPeriodToBackend, getCurrentFiscalYear } from '../../../shared/utils/fiscal-year.utils';

import styled from '@emotion/styled';

const TERTIALS = [
  { tertial: 1, label: 'Tertial 1', months: ['Sep', 'Oct', 'Nov', 'Dec'] },
  { tertial: 2, label: 'Tertial 2', months: ['Jan', 'Feb', 'Mar', 'Apr'] },
  { tertial: 3, label: 'Tertial 3', months: ['May', 'Jun', 'Jul', 'Aug'] }
];

const MONTH_MAP: { [key: string]: number } = {
  'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3,
  'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7,
  'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
};

interface FiscalYearPickerProps {
  control: Control<any>;
  name: string;
  initialValue?: string | null;
  onChange?: (value: string | null) => void;
  disablePastDates?: boolean;
}

const YearNavigation = ({ fiscalYear, onYearChange, minYear }: {
  fiscalYear: number;
  onYearChange: (year: number) => void;
  minYear?: number;
}) => {
  const handleClick = useCallback((e: React.MouseEvent, direction: 'prev' | 'next') => {
    e.preventDefault();
    e.stopPropagation();

    const newYear = direction === 'prev' ? fiscalYear - 1 : fiscalYear + 1;

    if (minYear && direction === 'prev' && newYear < minYear) {
      return;
    }

    onYearChange(newYear);
  }, [fiscalYear, onYearChange, minYear]);

  const isPrevDisabled = minYear ? fiscalYear <= minYear : false;

  return (
    <YearNavigationContainer>
      <YearButton
        onClick={(e) => handleClick(e, 'prev')}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={isPrevDisabled}
        style={{ pointerEvents: isPrevDisabled ? 'none' : 'auto' }}
      >
        &lt;
      </YearButton>
      <YearText>FY {fiscalYear}</YearText>
      <YearButton
        onClick={(e) => handleClick(e, 'next')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        &gt;
      </YearButton>
    </YearNavigationContainer>
  );
};

const MonthSelector = ({
  fiscalYear,
  tertial,
  month,
  selectedValue,
  onSelect,
  onCustomChange,
  isDisabled
}: {
  fiscalYear: number;
  tertial: number;
  month: string;
  selectedValue: string;
  onSelect: (value: string) => void;
  onCustomChange?: (value: string) => void;
  isDisabled?: boolean;
}) => {
  const fiscalPeriod = `${fiscalYear}-${tertial}-${month}`;
  const backendValue = fiscalPeriodToBackend(fiscalPeriod);
  const isSelected = selectedValue === backendValue;

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDisabled) return;

    onSelect(backendValue);
    if (onCustomChange) {
      onCustomChange(backendValue);
    }
  }, [backendValue, onSelect, onCustomChange, isDisabled]);

  return (
    <MonthButton
      type="button"
      onClick={handleClick}
      onMouseDown={(e) => e.stopPropagation()}
      isSelected={isSelected}
      disabled={isDisabled}
    >
      {month}
    </MonthButton>
  );
};

const TertialRowComponent = ({
  label,
  fiscalYear,
  tertial,
  months,
  selectedValue,
  onSelect,
  onCustomChange,
  disablePastDates,
  currentDate
}: {
  label: string;
  fiscalYear: number;
  tertial: number;
  months: string[];
  selectedValue: string;
  onSelect: (value: string) => void;
  onCustomChange?: (value: string) => void;
  disablePastDates?: boolean;
  currentDate: Date;
}) => {
  const isMonthDisabled = (month: string): boolean => {
    if (!disablePastDates) return false;
    const calendarYear = tertial === 1 ? fiscalYear : fiscalYear + 1;
    const monthIndex = MONTH_MAP[month];
    const monthDate = new Date(calendarYear, monthIndex + 1, 0);
    return monthDate < currentDate;
  };

  return (
    <TertialRow>
      <TertialLabel>{label}</TertialLabel>
      <MonthsContainer>
        {months.map(month => (
          <MonthSelector
            key={`${fiscalYear}-${month}`}
            fiscalYear={fiscalYear}
            tertial={tertial}
            month={month}
            selectedValue={selectedValue}
            onSelect={onSelect}
            onCustomChange={onCustomChange}
            isDisabled={isMonthDisabled(month)}
          />
        ))}
      </MonthsContainer>
    </TertialRow>
  );
};


const FiscalYearPicker: React.FC<FiscalYearPickerProps> = ({
  control,
  name,
  initialValue,
  onChange,
  disablePastDates = true
}) => {
  const initialFiscalPeriod = initialValue ? backendToFiscalPeriod(initialValue) : null;
  const initialFiscalYear = initialFiscalPeriod
    ? parseInt(initialFiscalPeriod.split('-')[0])
    : getCurrentFiscalYear();

  const [fiscalYear, setFiscalYear] = useState(initialFiscalYear);

  const currentDate = new Date();
  const currentFiscalYear = getCurrentFiscalYear();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FiscalPickerContainer>
          <CalendarContainer>
            <YearNavigation
              fiscalYear={fiscalYear}
              onYearChange={setFiscalYear}
              minYear={disablePastDates ? currentFiscalYear : undefined}
            />

            <Content>
              <TertialRowComponent
                label={TERTIALS[0].label}
                fiscalYear={fiscalYear}
                tertial={TERTIALS[0].tertial}
                months={TERTIALS[0].months}
                selectedValue={field.value}
                onSelect={field.onChange}
                onCustomChange={onChange}
                disablePastDates={disablePastDates}
                currentDate={currentDate}
              />

              <YearDivider>
                <YearLine />
                <CalendarYearText>({fiscalYear + 1})</CalendarYearText>
                <YearLine />
              </YearDivider>

              {TERTIALS.slice(1).map(({ tertial, label, months }) => (
                <TertialRowComponent
                  key={tertial}
                  label={label}
                  fiscalYear={fiscalYear}
                  tertial={tertial}
                  months={months}
                  selectedValue={field.value}
                  onSelect={field.onChange}
                  onCustomChange={onChange}
                  disablePastDates={disablePastDates}
                  currentDate={currentDate}
                />
              ))}
            </Content>
          </CalendarContainer>
        </FiscalPickerContainer>
      )}
    />
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 16px;
`;

export default FiscalYearPicker;
